import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import {
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceFailure } from '@confluence/macro-tracker/entry-points/MacroExperienceFailure.ts';
import { MacroExperienceSuccess } from '@confluence/macro-tracker/entry-points/MacroExperienceSuccess.ts';

import { useContributorsData } from './hooks/useContributorsData.ts';
import { useProcessedContributorsResult } from './hooks/useProcessedContributorsResult.ts';
import { useParsedMacroParams } from './hooks/useParsedMacroParams.ts';
import { i18n } from './contributorsMacroI18n.ts';
import { FlatLayout } from './FlatLayout.tsx';
import { ListLayout } from './ListLayout.tsx';

export type ContributorsMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: {
		include?: { value?: string };
		order?: { value?: string };
		reverse?: { value?: string };
		limit?: { value?: string };
		mode?: { value?: string };
		showAnonymous?: { value?: string };
		showCount?: { value?: string };
		showLastTime?: { value?: string };
		page?: { value?: string };
		labels?: { value?: string };
		label?: { value?: string };
		spaces?: { value?: string };
		space?: { value?: string };
		contentType?: { value?: string };
		publishDate?: { value?: string };
		scope?: { value?: string };
		noneFoundMessage?: { value?: string };
	};
};

const styles = cssMap({
	container: {
		display: 'inline-block',
	},
});

const ContributorsMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
}: ContributorsMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const { contentId, extensionKey, mode: macroMode, node } = macroDefaultProps;
	const attributes = macroDefaultProps.attributes ?? getMacroAttributesFromADFNode(node);
	const experienceName = getExperienceName(macroMode, node);

	const {
		include,
		order,
		reverse,
		limit,
		mode,
		showAnonymous,
		showCount,
		showTime,
		noneFoundMessage,
		macroParamsForQuery,
	} = useParsedMacroParams(macroParams);

	const { data, error, loading } = useContributorsData({
		contentId: macroDefaultProps.contentId,
		macroParams: macroParamsForQuery,
	});

	const { visibleContributors, hiddenContributors } = useProcessedContributorsResult({
		data,
		include,
		order,
		reverse,
		showAnonymous,
		limit,
	});

	if (loading) {
		return null;
	}

	if (error) {
		return (
			<>
				<MacroExperienceFailure
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={macroMode}
					attributes={attributes}
					error={error}
				/>
				<ErrorDisplay error={error} />
			</>
		);
	}

	if (!data || (visibleContributors.length === 0 && hiddenContributors.length === 0)) {
		const message =
			noneFoundMessage ||
			formatMessage(i18n.noContributorsDefault, {
				includeTypes: include.join(', '),
			});
		return (
			<>
				<MacroExperienceSuccess
					name={experienceName}
					contentId={contentId}
					extensionKey={extensionKey}
					mode={macroMode}
					attributes={attributes}
				/>
				<Box testId="contributors-empty-state">{message}</Box>
			</>
		);
	}

	const LayoutComponent = mode === 'list' ? ListLayout : FlatLayout;

	return (
		<>
			<MacroExperienceSuccess
				name={experienceName}
				contentId={contentId}
				extensionKey={extensionKey}
				mode={macroMode}
				attributes={attributes}
			/>
			<Box
				xcss={styles.container}
				testId="contributors-macro"
				data-fabric-macro={macroDefaultProps?.attributes?.macroId}
				data-macro-body=""
			>
				<LayoutComponent
					visibleContributors={visibleContributors}
					hiddenContributors={hiddenContributors}
					showCount={showCount}
					showTime={showTime}
				/>
			</Box>
		</>
	);
};

const renderErrorFallback = () => <FormattedMessage {...i18n.error} />;

export const ContributorsMacroRenderer = (
	props: ContributorsMacroRendererProps,
): React.JSX.Element => {
	return (
		<GenericErrorBoundary
			renderOnError={renderErrorFallback}
			attribution={Attribution.PAGE_EXPERIENCES}
		>
			<ContributorsMacroRendererComponent {...props} />
		</GenericErrorBoundary>
	);
};
